// The PixiJS world view. Owns the canvas, the iso board, every world sprite,
// the combat animation, and all the juice (particles, floating numbers,
// screenshake, flash). It reads the game state each frame to sync sprites and
// subscribes to gameplay events to fire feedback. Gameplay logic never touches
// Pixi; it only emits.
//
// Camera model: world sprites live inside `worldRoot`, a single container that
// applies the camera offset + zoom, so the whole wide board auto-fits any screen
// (phone to desktop) and every sprite scales together. Effects (particles,
// floaters) and the cursor live in screen space so numbers stay readable when
// the board is zoomed out.

import { Application, Container, Graphics, Sprite } from 'pixi.js';
import { camera, worldToLocal, worldToScreen, setCamera, depth, TILE_W, TILE_H } from './iso';
import {
  makeGround,
  makeNodeSprite,
  makeBuildingSprite,
  makeTownCenter,
  makeCursorCircle,
  drawCursorCircle,
  makeUnitSprite,
  makeWorkerSprite,
  makeDecorSprite,
  makeWolfSprite,
  workerVehicle,
  eraIndex,
  eraTint,
  ERA_GROUND,
  ERA_PATH,
  type NodeSprite,
  type UnitKind,
  type WorkerVehicle,
  type DecorKind,
} from './sprites';
import { bakeSky, shade } from './pixelart';
import { actorArt, actorAttack } from './actors';
import { animateStructure } from './structure-animation';
import { ageVisual, visualAge } from '@data/visual-ages';
import { ParticleSystem } from './particles';
import { FloaterSystem } from './floaters';
import { bus } from '@core/events';
import { fmt, clamp } from '@core/numbers';
import { RESOURCES } from '@data/resources';
import { BUILDINGS, type BuildingId } from '@data/buildings';
import { harvestRadius, autoCollectRates } from '@systems/stats';
import { resourceArt } from './atelier';
import { Citizens } from './citizens';
import { AgeTitle } from '../ui/age-title';
import { enemyKindAt } from '@data/enemies';
import { cursor } from '@systems/harvest';
import type { GameState } from '@core/state';
import { t } from '@i18n/i18n';

const GROUND_RADIUS = 9; // tiles from center the ground field extends to (wide, open)

function ringTiles(r: number): [number, number][] {
  if (r === 0) return [[0, 0]];
  const out: [number, number][] = [];
  for (let x = -r; x <= r; x++) {
    for (let y = -r; y <= r; y++) {
      if (Math.max(Math.abs(x), Math.abs(y)) === r) out.push([x, y]);
    }
  }
  return out;
}
/** Inner tile slots the settlement fills as it builds (rings 1 -> 4 minus the
 *  two diagonal axes = 64 slots). The axes stay clear as permanent streets from
 *  the keep to the board rim, so the town grows as four readable quarters
 *  instead of one blob, and couriers/armies march on infrastructure. */
const BUILDING_SLOTS: [number, number][] = [
  ...ringTiles(1),
  ...ringTiles(2),
  ...ringTiles(3),
  ...ringTiles(4),
].filter(([x, y]) => x !== 0 && y !== 0);

interface NodeRecord extends NodeSprite {
  gx: number;
  gy: number;
  lastDepleted: boolean;
  hitPunch: number;
  respawnPop: number;
  /** Pulsing "there is something here" ring under a live node. A recorded first
   *  session showed roughly two thirds of a new player's taps landing on empty
   *  grass with no feedback at all: 15 nodes are scattered over a ~17x17 field
   *  and the sprites read as scenery. The ring retires itself once auto-collect
   *  unlocks, so it teaches the core verb and then leaves the art alone. */
  hint: Graphics;
}

interface BuildingRecord {
  id: BuildingId;
  variant: number;
  age: number;
  root: Container;
  popT: number;
  chimneyH: number; // smoke emitter height above the building base
}

/** A marching combat unit (enemy or defender), animated in local coords. The
 *  root carries position/zIndex/fade and the ground shadow; the inner sprite
 *  carries the bob/lunge so the shadow stays planted while the body moves. */
interface Marcher {
  root: Container;
  spr: Sprite;
  kind: UnitKind;
  x: number;
  y: number;
  tx: number;
  ty: number;
  speed: number;
  phase: 'march' | 'fight' | 'rout' | 'overrun' | 'gone';
  bob: number;
  enemy: boolean;
  facing: number; // +1 / -1, the direction this unit lunges when fighting
}

type CourierResource = 'wood' | 'stone' | 'ore';

/** An auto-collection courier. Walks from the keep to the nearest matching node,
 *  gathers, and carries the load home. Purely visual: its load over a round trip
 *  equals the real auto-collect rate, but it never changes income. */
interface Courier {
  resource: CourierResource;
  root: Container;
  spr: Sprite;
  load: Graphics; // resource-coloured cargo, shown on the way back
  vehicle: WorkerVehicle;
  phase: 'out' | 'gather' | 'back';
  x: number;
  y: number;
  tx: number;
  ty: number;
  speed: number;
  gatherTimer: number;
  tripT: number; // elapsed round-trip time, so the load can mirror the real rate
  bob: number;
}

const COURIER_RESOURCES: CourierResource[] = ['wood', 'stone', 'ore'];
const COURIER_VALUE: Record<CourierResource, number> = { wood: 1, stone: 1.25, ore: 1.6 };

function courierSpeed(vehicle: WorkerVehicle): number {
  return ['drone','shuttle','energy'].includes(vehicle) ? 145 : vehicle === 'truck' || vehicle === 'rover' ? 115 : 70; // carts keep pace; only the motor era is faster
}
function courierGather(resource: CourierResource, vehicle: WorkerVehicle): number {
  const load = vehicle === 'truck' ? 1.9 : vehicle === 'cart' ? 1.5 : 1;
  return 2.4 * COURIER_VALUE[resource] * load; // valuable resource + bigger load = longer
}
function softTint(color: number): number {
  // blend halfway to white so the gatherer is resource-tinted, not fully recoloured
  const r = ((color >> 16) & 0xff) / 2 + 128;
  const g = ((color >> 8) & 0xff) / 2 + 128;
  const b = (color & 0xff) / 2 + 128;
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(b);
}

export class WorldView {
  app!: Application;

  private worldRoot = new Container(); // camera (offset + zoom) applied here
  private groundLayer = new Container();
  private objectLayer = new Container();
  private unitsLayer = new Container();
  private effectsLayer = new Container(); // screen space
  private shakeRoot = new Container();
  private cursorLayer = new Container();
  private flashOverlay = new Graphics();

  private cursorCircle = makeCursorCircle();
  private particles!: ParticleSystem;
  private floaters!: FloaterSystem;

  private nodeRecords = new Map<number, NodeRecord>();
  private nodeByTile = new Map<string, NodeRecord>();
  private buildingRecords: BuildingRecord[] = [];
  private placedBuildings = 0;
  private currentTier = 0;
  private currentAge = 0; // drives the era look of newly raised buildings + units
  private renderedAge = -1;
  private renderedTier = -1;
  private animationClock = 0;
  private artFrame = -1;
  private projectiles: { x:number; y:number; tx:number; ty:number; t:number; kind:string; color:number }[] = [];
  private projectileInk = new Graphics();
  private shotTimer = 0;
  private citizens!: Citizens;
  private ageTitle!: AgeTitle;
  private transition: { state: GameState; time: number; oldAge: number; rolled: boolean } | null = null;
  private oldGround: Container | null = null;
  private oldSky: Sprite | null = null;
  private titleTime = -1;
  private reducedMotion = false;
  private currentEra = -1; // when this changes, the keep is re-skinned to the new era

  private marchers: Marcher[] = [];
  private couriers: Courier[] = []; // auto-collection gatherers (one per basic resource)
  private lastGameTime = -1; // for sim-time deltas (couriers must not earn during pauses)
  private canvasRect: DOMRect | null = null; // cached; invalidated on resize
  private lastCursorRadius = -1; // cursor circle geometry cache (redraw only on change)
  private lastCursorHot = false;
  private battleX = 0; // local coords of the clash line (for fight sparks)
  private battleY = 0;
  private fightSparkTimer = 0;
  private floaterBudget = 0; // global combat-floater spawn allowance (~6/sec)
  private fightersScratch: Marcher[] = []; // per-frame scratch (updateUnits), reused to avoid GC churn

  private trauma = 0;
  private flashColor = 0xffffff;
  private flashAlpha = 0;
  private flashDrawn = false;

  // Environment: era sky backdrop, ground era, stagger-wave rebuild animation.
  private skySprite?: Sprite;
  private groundEra = -1;
  private groundAge = -1;
  private groundAnim: { spr: Sprite; delay: number; t: number }[] = [];
  private keepPopT = -1; // era-dawn ceremony: the keep re-pops with easeOutBack

  // Ambient life: chimney smoke rotation, boss dusk + stomps, march dust, wolf.
  private smokeTimer = 0;
  private smokeIdx = 0;
  private duskOverlay?: Graphics;
  private duskAlpha = 0;
  private duskTarget = 0;
  private stompTimer = 0;
  private dustTimer = 0;
  private wolf: {
    root: Container;
    spr: Sprite;
    x: number;
    y: number;
    tx: number;
    ty: number;
    speed: number;
    pauseT: number;
    howled: boolean;
    half: number; // distance at which the mid-path howl triggers
    bob: number;
  } | null = null;
  private wolfIdleTimer = 120;

  async init(host: HTMLElement): Promise<void> {
    this.app = new Application();
    await this.app.init({
      background: 0x10131b,
      antialias: false, // crisp pixels
      resizeTo: window,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });
    host.appendChild(this.app.canvas);

    this.objectLayer.sortableChildren = true;
    this.unitsLayer.sortableChildren = true;
    this.worldRoot.addChild(this.groundLayer, this.objectLayer, this.unitsLayer, this.projectileInk);
    this.shakeRoot.addChild(this.worldRoot, this.effectsLayer);
    this.app.stage.addChild(this.shakeRoot, this.cursorLayer, this.flashOverlay);

    this.cursorLayer.addChild(this.cursorCircle);
    this.particles = new ParticleSystem(this.effectsLayer);
    this.floaters = new FloaterSystem(this.effectsLayer);

    this.recenter();
    this.refreshEnvironment(0);

    // Boss dusk: a dark veil between the world and the effects, faded in while
    // a boss marches so the climax literally darkens the land.
    this.duskOverlay = new Graphics();
    this.drawDusk();
    this.duskOverlay.alpha = 0;
    this.shakeRoot.addChildAt(this.duskOverlay, 1);

    this.citizens = new Citizens(this.objectLayer);
    this.ageTitle = new AgeTitle();
    this.app.canvas.addEventListener('pointerdown', () => this.skipTransition());
    this.bindEvents();

    this.app.renderer.on('resize', () => this.onResize());
  }

  // ---- camera / layout ----

  private recenter(): void {
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    // Auto-fit zoom so the whole wide board frames on any device, with margin
    // for tall building sprites and the bottom HUD.
    const boardW = 2 * GROUND_RADIUS * TILE_W + TILE_W * 2;
    const boardH = 2 * GROUND_RADIUS * TILE_H + TILE_H * 8;
    const portrait = h > w * 1.35;
    if (portrait) {
      // 9:16 phones: the HUD stacks into a top band (~215px) and a bottom band
      // (~140px). Let the diamond's left/right tips overflow slightly (they are
      // empty corners) so the board fills the free middle band instead of
      // floating small in it.
      const zoom = clamp(Math.min((w * 1.26) / boardW, (h * 0.5) / boardH), 0.42, 1.15);
      setCamera(w / 2, h * 0.53, zoom);
    } else {
      const zoom = clamp(Math.min(w / boardW, h / boardH), 0.38, 1.15);
      setCamera(w / 2, h / 2 - TILE_H * 1.2 * zoom, zoom);
    }
    this.worldRoot.position.set(camera.x, camera.y);
    this.worldRoot.scale.set(camera.zoom);
  }

  private onResize(): void {
    if (this.oldSky) {
      this.oldSky.width = this.app.screen.width;
      this.oldSky.height = this.app.screen.height;
    }
    this.canvasRect = null; // re-measure lazily (layout may still be settling)
    this.recenter();
    if (this.skySprite) this.refreshSky(this.groundEra < 0 ? 0 : this.groundEra,
      this.transition && !this.transition.rolled ? this.transition.oldAge : this.currentAge);
    this.drawDusk();
  }

  /** (Re)draws the dusk veil to cover the screen (with shake margin). */
  private drawDusk(): void {
    if (!this.duskOverlay) return;
    this.duskOverlay
      .clear()
      .rect(-60, -60, this.app.screen.width + 120, this.app.screen.height + 120)
      .fill({ color: 0x060814 });
  }

  private townCenter?: Container;

  /** (Re)builds the whole ground for an era: the soil slab under the island's
   *  lower rim, era-tinted tiles with a soft checker, the two axis streets, and
   *  scattered decor. With `animate`, tiles pop in a wave rolling out from the
   *  keep, the visible "the world itself changes" beat of an era dawn. */
  private buildGround(era: number, animate = false): void {
    this.groundEra = era;
    this.groundAge = visualAge(this.currentAge);
    this.groundAnim = [];
    if (animate) {
      this.oldGround = new Container();
      this.oldGround.addChild(...this.groundLayer.removeChildren());
      this.worldRoot.addChildAt(this.oldGround, 0);
    }
    for (const c of [...this.groundLayer.children]) c.destroy();
    this.groundLayer.removeChildren();

    // Island edge: extrude the two lower rims down as dark soil so the board
    // stops reading as a floating carpet with a razor edge.
    const R = GROUND_RADIUS;
    const left = worldToLocal(-R, R);
    const bottom = worldToLocal(R, R);
    const right = worldToLocal(R, -R);
    const th = TILE_H / 2;
    const depth = 14;
    const earth = era >= 4 ? 0x2a2836 : 0x33241a;
    const lip = era >= 4 ? 0x3a3750 : 0x4c2f1b;
    const slab = new Graphics();
    slab
      .poly([
        left.x, left.y + th,
        bottom.x, bottom.y + th,
        right.x, right.y + th,
        right.x, right.y + th + depth,
        bottom.x, bottom.y + th + depth,
        left.x, left.y + th + depth,
      ])
      .fill({ color: earth });
    slab
      .poly([
        left.x, left.y + th,
        bottom.x, bottom.y + th,
        right.x, right.y + th,
        right.x, right.y + th + 3,
        bottom.x, bottom.y + th + 3,
        left.x, left.y + th + 3,
      ])
      .fill({ color: lip });
    // A few darker strata speckles for texture.
    for (let i = 0; i < 14; i++) {
      const f = i / 14;
      const sx = left.x + (right.x - left.x) * f + ((i * 37) % 11) - 5;
      const sy = (f < 0.5 ? left.y + (bottom.y - left.y) * (f * 2) : bottom.y + (right.y - bottom.y) * (f - 0.5) * 2) + th + 5 + ((i * 53) % 6);
      slab.rect(sx, sy, 3, 2).fill({ color: shade(earth, 0.7) });
    }
    this.groundLayer.addChild(slab);

    const lateGround = [0x646052,0x4c6570,0x4b405f,0x899faa,0xa09a7c];
    const base = this.currentAge>=7 ? lateGround[visualAge(this.currentAge)-7] : ERA_GROUND[Math.max(0, Math.min(era, ERA_GROUND.length - 1))];
    const path = this.currentAge>=7 ? shade(base,1.22) : ERA_PATH[Math.max(0, Math.min(era, ERA_PATH.length - 1))];

    for (let gx = -R; gx <= R; gx++) {
      for (let gy = -R; gy <= R; gy++) {
        if (Math.max(Math.abs(gx), Math.abs(gy)) > R) continue;
        const isRoad = gx === 0 || gy === 0;
        // Soft checker (0.95, not two loud greens) so the grid whispers.
        const tone = isRoad ? path : base;
        const color = (gx + gy) % 2 === 0 ? tone : shade(tone, 0.95);
        const tile = makeGround(color, (gx * 7 + gy * 13) & 0xffff);
        const p = worldToLocal(gx, gy);
        tile.position.set(p.x, p.y);
        this.groundLayer.addChild(tile);
        const anim = animate
          ? [{ spr: tile, delay: (Math.abs(gx) + Math.abs(gy)) * (1.6 / 18), t: 0 }]
          : [];

        // Scatter decor outside the building quarters and off the streets.
        if (!isRoad && Math.max(Math.abs(gx), Math.abs(gy)) > 4) {
          let n = ((gx + 31) * 2654435761) ^ ((gy + 31) * 40503);
          n = (n ^ (n << 13)) >>> 0;
          n = (n ^ (n >> 17)) >>> 0;
          const roll = (n >>> 0) / 0xffffffff;
          let kind: DecorKind | null = null;
          if (roll < 0.08) kind = era >= 4 ? 'crystal' : 'flower';
          else if (roll < 0.14) kind = era >= 4 ? 'crystal' : 'tuft';
          else if (roll < 0.18) kind = 'pebble';
          if (kind) {
            const d = makeDecorSprite(kind);
            d.position.set(p.x + ((n >> 8) % 13) - 6, p.y + ((n >> 16) % 7) - 3);
            this.groundLayer.addChild(d);
            if (animate) anim.push({ spr: d, delay: (Math.abs(gx) + Math.abs(gy)) * (1.6 / 18), t: 0 });
          }
        }
        if (animate) {
          for (const a of anim) { a.spr.scale.y = 0; a.spr.alpha = 0; }
          this.groundAnim.push(...anim);
        }
      }
    }
  }

  /** Stretches the era sky across the screen behind the world. Re-baked on era
   *  change and resize (the horizon follows where the board sits on screen). */
  private refreshSky(era: number, age = this.currentAge): void {
    const tex = bakeSky(era, camera.y / Math.max(1, this.app.screen.height), this.app.screen.width / Math.max(1, this.app.screen.height), age);
    if (!this.skySprite) {
      this.skySprite = new Sprite(tex);
      this.app.stage.addChildAt(this.skySprite, 0);
    } else {
      this.skySprite.texture = tex;
    }
    this.skySprite.width = this.app.screen.width;
    this.skySprite.height = this.app.screen.height;
  }

  /** Ground + sky follow the era. Cheap when the era is unchanged. */
  private refreshEnvironment(age: number, animate = false): void {
    const era = eraIndex(age);
    if (era !== this.groundEra || visualAge(age) !== this.groundAge) this.buildGround(era, animate);
    this.refreshSky(era);
  }

  // ---- (re)build from state ----

  rebuildFromState(state: GameState): void {
    this.cancelTransition();
    this.citizens.reset();
    if (state.run.gatePending || state.run.combatResolveAt > 0) this.citizens.shelter(true);
    this.lastGameTime = state.gameTime;
    this.currentTier = state.run.settlementTier;
    this.renderedTier = this.currentTier;
    this.renderedAge = visualAge(state.run.age);
    this.currentAge = state.run.age;
    this.artFrame = -1;
    this.refreshEnvironment(state.run.age); // no ceremony on load/restart
    this.placeKeep(state.run.age);
    this.rebuildNodes(state);
    this.rebuildBuildings(state);
    this.clearMarchers();
    this.clearCouriers();
    this.citizens.update(state.run.population, state.run.age, 0, this.buildingRecords, false);
  }

  /** (Re)creates the keep in the given age's era look. Cheap (one sprite), so it
   *  is also called when the settlement crosses into a new era. */
  private placeKeep(age: number): void {
    if (this.townCenter) this.townCenter.destroy({ children: true });
    this.townCenter = makeTownCenter(age, this.currentTier);
    const p = worldToLocal(0, 0);
    this.townCenter.position.set(p.x, p.y);
    this.townCenter.zIndex = depth(0, 0) * 10;
    this.objectLayer.addChild(this.townCenter);
    this.currentEra = eraIndex(age);
  }

  /** Live-only, non-blocking presentation. No simulation state is written. */
  private eraDawn(state: GameState): void {
    this.cancelTransition();
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.titleTime = 0;
    if (this.reducedMotion) {
      this.reskinSettlement(state);
      this.rebuildNodes(state);
      this.refreshEnvironment(state.run.age);
      this.settlePops();
    } else {
      this.transition = { state, time: 0, oldAge: this.buildingRecords[0]?.age ?? this.currentAge - 1, rolled: false };
    }
  }

  private cancelTransition(): void {
    this.transition = null;
    this.oldGround?.destroy({ children: true }); this.oldGround = null;
    this.oldSky?.destroy(); this.oldSky = null;
    for (const a of this.groundAnim) { a.spr.scale.set(1); a.spr.alpha = 1; }
    this.groundAnim.length = 0;
    for (const rec of this.buildingRecords) rec.root.rotation = 0;
    this.titleTime = -1;
    this.ageTitle?.hide();
    this.keepPopT = -1;
    if (this.duskOverlay) this.duskOverlay.alpha = this.duskAlpha;
  }

  private settlePops(): void {
    for (const rec of this.buildingRecords) { rec.popT = 1; rec.root.scale.set(1); rec.root.rotation = 0; }
    this.townCenter?.scale.set(1);
    this.keepPopT = -1;
  }

  skipTransition(): void {
    if (this.transition) this.finishTransition();
    this.titleTime = -1;
    this.ageTitle.hide();
  }

  private finishTransition(): void {
    const state = this.transition?.state;
    if (!state) return;
    const titleTime = this.titleTime;
    this.cancelTransition();
    this.reskinSettlement(state);
    this.rebuildNodes(state);
    this.refreshEnvironment(state.run.age);
    this.settlePops();
    this.titleTime = titleTime;
    this.updateAgeAnimation();
    this.citizens.update(state.run.population, state.run.age, 0, this.buildingRecords, false);
    this.duskAlpha = this.duskTarget;
    if (this.duskOverlay) this.duskOverlay.alpha = this.duskAlpha;
  }

  private reskinBuilding(rec: BuildingRecord, age: number): void {
    const old = rec.root;
    const root = makeBuildingSprite(rec.id, this.currentTier, age, rec.variant);
    root.position.copyFrom(old.position);
    root.zIndex = old.zIndex;
    this.objectLayer.addChild(root);
    old.destroy({ children: true });
    rec.root = root; rec.age = age; rec.popT = 0; rec.chimneyH = root.height * .9;
  }

  private updateTransition(dt: number): void {
    if (this.titleTime >= 0) {
      this.titleTime += dt;
      this.ageTitle.show(this.currentAge, this.titleTime);
      if (this.titleTime >= 3.5) this.titleTime = -1;
    }
    const tr = this.transition;
    if (!tr) return;
    tr.time += dt;
    if (tr.time < .8) {
      for (const rec of this.buildingRecords) rec.root.rotation = Math.sin(tr.time * 38 + rec.variant) * .008;
    } else {
      if (!tr.rolled) {
        tr.rolled = true;
        this.currentTier = tr.state.run.settlementTier;
        this.placeKeep(this.currentAge); this.keepPopT = 0;
        if (this.skySprite) {
          this.oldSky = new Sprite(this.skySprite.texture);
          this.oldSky.width = this.app.screen.width; this.oldSky.height = this.app.screen.height;
          this.app.stage.addChildAt(this.oldSky, 1);
        }
        this.refreshEnvironment(this.currentAge, true);
        this.addFlash(eraTint(this.currentAge), .12); this.addShake(.12);
      }
      if (this.oldSky) this.oldSky.alpha = Math.max(0, 1 - (tr.time - .8) / 1.8);
      for (const rec of this.buildingRecords) {
        rec.root.rotation = 0;
        const [gx, gy] = BUILDING_SLOTS[rec.variant];
        if (rec.age !== this.currentAge && tr.time >= .8 + (Math.abs(gx) + Math.abs(gy)) * (1.6 / 18)) {
          this.reskinBuilding(rec, this.currentAge);
          this.particles.burst(camera.x + rec.root.x * camera.zoom, camera.y + rec.root.y * camera.zoom,
            { count: 5, color: 0xd1a56e, speed: 45, life: .45, size: .7 });
        }
      }
      for (const node of tr.state.run.nodes) {
        if (tr.time >= .8 + (Math.abs(node.gx) + Math.abs(node.gy)) * (1.6 / 18)) {
          const rec = this.nodeRecords.get(node.id);
          if (rec) (rec.shape as Sprite).texture = resourceArt(node.resource, this.currentAge);
        }
      }
    }
    if (this.duskOverlay) this.duskOverlay.alpha = Math.max(this.duskAlpha, .3 * Math.min(1, tr.time / .5) * Math.max(0, 1 - Math.max(0, tr.time - .8) / 1.8));
    if (tr.time >= 3.5) this.finishTransition();
  }

  /** Re-skin in place: construction order, tile, count and simulation stay intact. */
  private reskinSettlement(state: GameState): void {
    this.currentTier = state.run.settlementTier;
    this.renderedTier = this.currentTier;
    this.placeKeep(state.run.age);
    for (const rec of this.buildingRecords) this.reskinBuilding(rec, state.run.age);
    for (const m of this.marchers) m.spr.texture = actorArt(m.kind, state.run.age);
    this.clearCouriers();
    if (this.wolf && state.run.age>=5) { this.wolf.root.destroy({children:true}); this.wolf=null; }
    this.projectiles=[];
    this.projectileInk.clear();
    this.artFrame = -1;
  }

  private updateAgeAnimation(): void {
    const frame = Math.floor(this.animationClock * 8);
    if (frame === this.artFrame) return;
    this.artFrame = frame;
    for (const rec of this.buildingRecords) animateStructure(rec.root,rec.id,rec.age,frame+rec.variant);
    if (this.townCenter) animateStructure(this.townCenter,'keep',this.transition && !this.transition.rolled ? this.transition.oldAge : this.currentAge,frame);
    for (const m of this.marchers) m.spr.texture=actorArt(m.kind,this.currentAge,frame,m.phase==='fight'?'attack':'move');
    for (const c of this.couriers) c.spr.texture=actorArt('worker',this.currentAge,Math.floor(c.bob * 8 / 9));
  }

  /** Visual projectiles only. Combat outcome is still owned by the simulation. */
  private updateProjectiles(dt:number):void {
    const ageAttack=ageVisual(this.currentAge).attack;
    this.shotTimer-=dt;
    if(ageAttack!=='melee' && this.shotTimer<=0) {
      this.shotTimer=.22;
      const fighters=this.marchers.filter(m=>m.phase==='fight');
      const source=fighters[Math.floor(Math.random()*fighters.length)];
      const target=source?fighters.find(m=>m.enemy!==source.enemy):undefined;
      const attack=source?actorAttack(source.kind,this.currentAge):ageAttack;
      if(attack==='cannon')this.shotTimer=.55;
      if(source&&target&&this.projectiles.length<20)this.projectiles.push({x:source.x,y:source.y-14,tx:target.x,ty:target.y-12,t:0,kind:attack,color:source.enemy?0xed8574:attack==='arrow'?0xd1a56e:attack==='cannon'||attack==='rifle'?0xffd27a:0x7fd4dc});
    }
    this.projectileInk.clear();
    for(const p of this.projectiles){
      p.t+=dt;const k=Math.min(1,p.t/.3),x=p.x+(p.tx-p.x)*k,y=p.y+(p.ty-p.y)*k-(p.kind==='arrow'?Math.sin(k*Math.PI)*12:0);
      if(p.kind==='beam')this.projectileInk.moveTo(p.x,p.y).lineTo(p.tx,p.ty).stroke({width:2,color:p.color,alpha:1-k});
      else if(p.kind==='cannon')this.projectileInk.rect(Math.round(x),Math.round(y),4,4).fill(p.color);
      else this.projectileInk.moveTo(x,y).lineTo(x-(p.tx-p.x)*.16,y-(p.ty-p.y)*.16).stroke({width:2,color:p.color});
    }
    this.projectiles=this.projectiles.filter(p=>p.t<.3);
  }

  private rebuildNodes(state: GameState): void {
    for (const rec of this.nodeRecords.values()) rec.root.destroy({ children: true });
    this.nodeRecords.clear();
    this.nodeByTile.clear();

    for (const node of state.run.nodes) {
      const sprite = makeNodeSprite(node.resource, state.run.age);
      // Ground ring in the resource's own colour, drawn behind the sprite.
      // Sized off the tile, not guessed in pixels: it has to read as "this TILE
      // holds something" from a phone's viewing distance, so it sits a little
      // wider than the node's own shadow (TILE_W * 0.26).
      const hint = new Graphics();
      const def = RESOURCES[node.resource];
      const hw = TILE_W * 0.4;
      const hh = TILE_H * 0.36;
      hint.ellipse(0, 0, hw, hh).fill({ color: def.color, alpha: 0.22 });
      hint.ellipse(0, 0, hw, hh).stroke({ width: 2, color: def.color, alpha: 1 });
      hint.ellipse(0, 0, hw * 0.62, hh * 0.62).stroke({ width: 1, color: 0xffffff, alpha: 0.5 });
      sprite.root.addChildAt(hint, 0);
      const rec: NodeRecord = {
        ...sprite,
        gx: node.gx,
        gy: node.gy,
        lastDepleted: node.depleted,
        hitPunch: 0,
        respawnPop: 1,
        hint,
      };
      const p = worldToLocal(node.gx, node.gy);
      rec.root.position.set(p.x, p.y);
      rec.root.zIndex = depth(node.gx, node.gy) * 10 + 1;
      this.objectLayer.addChild(rec.root);
      this.nodeRecords.set(node.id, rec);
      this.nodeByTile.set(`${node.gx},${node.gy}`, rec);
    }
  }

  private rebuildBuildings(state: GameState): void {
    for (const rec of this.buildingRecords) rec.root.destroy({ children: true });
    this.buildingRecords = [];
    this.placedBuildings = 0;
    for (const id of Object.keys(state.run.buildings) as BuildingId[]) {
      const lvl = state.run.buildings[id];
      for (let i = 0; i < lvl; i++) this.placeBuilding(id, true);
    }
  }

  private placeBuilding(id: BuildingId, instant = false): void {
    if (this.placedBuildings >= BUILDING_SLOTS.length) {
      this.placedBuildings++;
      return;
    }
    const [gx, gy] = BUILDING_SLOTS[this.placedBuildings];
    const root = makeBuildingSprite(id, this.currentTier, this.currentAge, this.placedBuildings);
    const p = worldToLocal(gx, gy);
    root.position.set(p.x, p.y);
    root.zIndex = depth(gx, gy) * 10 + 2;
    this.objectLayer.addChild(root);
    this.buildingRecords.push({ id, age: this.currentAge, variant: this.placedBuildings, root, popT: instant ? 1 : 0, chimneyH: root.height * 0.9 });
    this.placedBuildings++;
    if (!instant) this.citizens.build(this.buildingRecords.length - 1);
  }

  // ---- auto-collection couriers ----

  private clearCouriers(): void {
    for (const c of this.couriers) c.root.destroy({ children: true });
    this.couriers = [];
  }

  /** Nearest non-depleted node of a resource, in local coords (from the keep). */
  private pickNode(state: GameState, res: CourierResource): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (const node of state.run.nodes) {
      if (node.resource !== res || node.depleted) continue;
      const p = worldToLocal(node.gx, node.gy);
      const d = p.x * p.x + p.y * p.y;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best;
  }

  private spawnCourier(state: GameState, res: CourierResource, vehicle: WorkerVehicle): Courier | null {
    const node = this.pickNode(state, res);
    if (!node) return null;
    const home = worldToLocal(0, 0);
    const root = new Container();
    const spr = makeWorkerSprite(vehicle, this.currentAge);
    spr.tint = softTint(RESOURCES[res].color);
    const shadow = new Graphics();
    shadow
      .ellipse(0, spr.height * 0.15, spr.width * 0.38, spr.width * 0.15)
      .fill({ color: 0x000000, alpha: 0.2 });
    root.addChild(shadow, spr);
    const load = new Graphics();
    load.rect(-3, -15, 6, 6).fill({ color: RESOURCES[res].color });
    load.visible = false;
    root.addChild(load);
    root.x = home.x;
    root.y = home.y;
    this.unitsLayer.addChild(root);
    return {
      resource: res,
      root,
      spr,
      load,
      vehicle,
      phase: 'out',
      x: home.x,
      y: home.y,
      tx: node.x,
      ty: node.y,
      speed: courierSpeed(vehicle),
      gatherTimer: 0,
      tripT: 0,
      bob: Math.random() * 6,
    };
  }

  /** Spawns / retires / drives the gatherers so their visible loop mirrors the
   *  live auto-collect rate (load carried home = rate x round-trip time). */
  private updateWorkers(state: GameState, dt: number, simDt: number): void {
    const rates = autoCollectRates(state);
    const vehicle = workerVehicle(state.run.age);
    for (const res of COURIER_RESOURCES) {
      const rate = rates[res];
      let c = this.couriers.find((q) => q.resource === res);
      if (!state.run.autoUnlocked || rate <= 0) {
        if (c) {
          c.root.destroy({ children: true });
          this.couriers = this.couriers.filter((q) => q !== c);
        }
        continue;
      }
      if (!c) {
        const made = this.spawnCourier(state, res, vehicle);
        if (!made) continue;
        this.couriers.push(made);
        c = made;
      }
      if (c.vehicle !== vehicle) {
        // era changed: swap the vehicle sprite in place (above the shadow at
        // index 0, below the cargo load on top)
        c.spr.destroy();
        c.spr = makeWorkerSprite(vehicle, this.currentAge);
        c.spr.tint = softTint(RESOURCES[res].color);
        c.root.addChildAt(c.spr, 1);
        c.vehicle = vehicle;
        c.speed = courierSpeed(vehicle);
      }
      this.advanceCourier(state, c, rate, dt, simDt);
    }
  }

  private advanceCourier(state: GameState, c: Courier, rate: number, dt: number, simDt: number): void {
    c.bob += dt * 9;
    // Trip time accrues on SIM time, not frame time: while the game is paused
    // (card choice / death screen) no income flows, so the delivery floater
    // must not keep counting or it would credit phantom resources.
    c.tripT += simDt;

    if (c.phase === 'gather') {
      c.gatherTimer -= dt;
      if (c.gatherTimer <= 0) {
        const home = worldToLocal(0, 0);
        c.tx = home.x + (Math.random() - 0.5) * 22;
        c.ty = home.y + (Math.random() - 0.5) * 12;
        c.phase = 'back';
        c.load.visible = true;
      }
    } else {
      const dx = c.tx - c.x;
      const dy = c.ty - c.y;
      const dist = Math.hypot(dx, dy);
      const step = c.speed * dt;
      if (dist > step) {
        c.x += (dx / dist) * step;
        c.y += (dy / dist) * step;
        c.spr.scale.x = (dx >= 0 ? 1 : -1) * Math.abs(c.spr.scale.x);
      } else {
        c.x = c.tx;
        c.y = c.ty;
        if (c.phase === 'out') {
          c.phase = 'gather';
          c.gatherTimer = courierGather(c.resource, c.vehicle);
        } else {
          // delivered: the load mirrors the real rate over this whole trip
          const amount = rate * c.tripT;
          const s = worldToScreen(0, 0);
          this.floaters.spawn(
            s.x,
            s.y - 16 * camera.zoom,
            `+${fmt(amount)} ${RESOURCES[c.resource].icon}`,
            { color: RESOURCES[c.resource].color, size: 15 },
          );
          this.particles.burst(s.x, s.y - 4 * camera.zoom, {
            color: RESOURCES[c.resource].color,
            count: 6,
            speed: 70,
            life: 0.5,
          });
          c.load.visible = false;
          c.tripT = 0;
          const node = this.pickNode(state, c.resource);
          if (node) {
            c.tx = node.x;
            c.ty = node.y;
            c.phase = 'out';
          } else {
            c.gatherTimer = 1.2;
            c.phase = 'gather';
          }
        }
      }
    }

    c.root.x = c.x;
    const hover = ['drone','shuttle','energy'].includes(c.vehicle);
    c.root.y = c.y;
    c.spr.y = hover ? -5 + Math.round(Math.sin(c.bob*.4)*2) : c.vehicle==='person'||c.vehicle==='horse' ? Math.round(Math.sin(c.bob)) : 0; // constant screen-px walk bob
    c.root.zIndex = c.y;
  }

  // ---- combat animation ----

  private clearMarchers(): void {
    this.projectiles = [];
    this.projectileInk.clear();
    for (const m of this.marchers) m.root.destroy({ children: true });
    this.marchers = [];
  }

  private spawnUnit(kind: UnitKind, x: number, y: number, tx: number, ty: number, enemy: boolean): void {
    const spr = makeUnitSprite(kind, this.currentAge);
    const facing = tx - x >= 0 ? 1 : -1;
    spr.scale.x = facing * Math.abs(spr.scale.x); // face travel direction (toward the foe)

    // Ground shadow (matches the buildings/nodes) so units stand ON the field
    // instead of floating over it; it stays planted while the body bobs.
    const root = new Container();
    const shadow = new Graphics();
    shadow
      .ellipse(0, spr.height * 0.15, spr.width * 0.4, spr.width * 0.16)
      .fill({ color: 0x000000, alpha: 0.2 });
    root.addChild(shadow, spr);
    root.x = x;
    root.y = y;
    this.unitsLayer.addChild(root);

    this.marchers.push({
      root,
      spr,
      kind,
      x,
      y,
      tx,
      ty,
      speed: 110 + Math.random() * 40, // reach the line quickly, then trade blows
      phase: 'march',
      bob: Math.random() * 6,
      enemy,
      facing,
    });
  }

  /** A wave is telegraphed: enemies march in from a random edge, defenders form
   *  a line to meet them. */
  private spawnWave(wave: number, isBoss: boolean): void {
    this.clearMarchers();
    const angle = Math.random() * Math.PI * 2;
    const dir = { x: Math.cos(angle), y: Math.sin(angle) * 0.6 };
    const perp = { x: -dir.y, y: dir.x };
    // The clash happens out in the open field (not on top of the settlement) so
    // the battle is clearly visible.
    const SPAWN = 460;
    const CLASH = 250;
    const DEFEND = 215;
    this.battleX = dir.x * ((CLASH + DEFEND) / 2);
    this.battleY = dir.y * ((CLASH + DEFEND) / 2);
    this.fightSparkTimer = 0;

    const enemyCount = isBoss ? 5 : clamp(4 + Math.floor(wave / 2), 4, 16);
    // Enemy KIND scales with ERA, not the raw wave index (see enemyKindAt): a
    // low age fields foot raiders, later eras field war machines, so the menace
    // on screen matches the age-based threat.
    const era = eraIndex(this.currentAge);
    for (let i = 0; i < enemyCount; i++) {
      const off = (i - enemyCount / 2) * 16;
      const kind: UnitKind = enemyKindAt(era, i, enemyCount, isBoss);
      // The boss looms: it spawns farther out and marches at half pace, so it
      // arrives last, alone, with a few seconds of solo screen presence.
      const spawnDist = kind === 'boss' ? SPAWN + 120 : SPAWN + Math.random() * 40;
      const sx = dir.x * spawnDist + perp.x * off;
      const sy = dir.y * spawnDist + perp.y * off;
      const tx = dir.x * CLASH + perp.x * off;
      const ty = dir.y * CLASH + perp.y * off;
      this.spawnUnit(kind, sx, sy, tx, ty, true);
      if (kind === 'boss') this.marchers[this.marchers.length - 1].speed = 40;
    }

    const defCount = isBoss ? 6 : 5;
    for (let i = 0; i < defCount; i++) {
      const off = (i - defCount / 2) * 16;
      const dx = dir.x * DEFEND + perp.x * off;
      const dy = dir.y * DEFEND + perp.y * off;
      // defenders start nearer the keep and advance to the line
      this.spawnUnit('defender', dx * 0.4, dy * 0.4, dx, dy, false);
    }
  }

  /** The fight resolved: enemies rout (win) or overrun the keep (loss). */
  private resolveCombat(win: boolean): void {
    for (const m of this.marchers) {
      if (m.enemy) {
        if (win) {
          // routed: flee back out and fade
          m.phase = 'rout';
          m.tx = m.x * 2.4;
          m.ty = m.y * 2.4;
          m.speed = 160;
        } else {
          // overrun: surge onto the keep then fade
          m.phase = 'overrun';
          m.tx = (Math.random() - 0.5) * 30;
          m.ty = (Math.random() - 0.5) * 20;
          m.speed = 140;
        }
      } else {
        // defenders: fall back to the keep and fade either way (never linger on
        // the field after the battle).
        m.phase = 'rout';
        m.tx = (Math.random() - 0.5) * 36;
        m.ty = (Math.random() - 0.5) * 22;
        m.speed = 120;
      }
    }
    const clash = this.marchers.find((m) => m.enemy);
    if (clash) {
      this.particles.burst(camera.x + clash.x * camera.zoom, camera.y + clash.y * camera.zoom, {
        color: win ? 0x6ad27a : 0xff5a5a,
        count: win ? 22 : 36,
        speed: 200,
        life: 0.8,
        size: 1.2,
      });
    }
  }

  /** A single floating combat number/word above a fighting unit (screen space).
   *  Enemies bleed gold (the defenders' blows), defenders bleed red. */
  private spawnCombatFloater(m: Marcher): void {
    const sx = camera.x + m.x * camera.zoom;
    const sy = camera.y + m.y * camera.zoom - 26;
    const roll = Math.random();
    if (roll < 0.1) {
      this.floaters.spawn(sx, sy, t('combat.dodge'), { color: 0x9fd0ff, size: 13, life: 0.7 });
    } else if (roll < 0.16) {
      this.floaters.spawn(sx, sy, t('combat.miss'), { color: 0xb8c0cc, size: 12, life: 0.6 });
    } else if (roll < 0.22) {
      this.floaters.spawn(sx, sy, t('combat.block'), { color: 0x9fd0ff, size: 12, life: 0.6 });
    } else {
      const crit = Math.random() < 0.18;
      const dmg = Math.floor((crit ? 18 : 5) + Math.random() * (crit ? 28 : 18));
      const color = crit ? 0xffe08a : m.enemy ? 0xffd24a : 0xff6a6a;
      this.floaters.spawn(sx, sy, (crit ? '✦' : '') + dmg, {
        color,
        size: crit ? 23 : 15,
        life: crit ? 1.0 : 0.8,
        vy: -54,
      });
    }
  }

  // ---- ambient life + ceremonies (per frame) ----

  /** The bozkurt crosses an outer lane, pauses mid-path to howl at the eternal
   *  sky, and leaves. One at a time; spawned on held waves and long idles. */
  private maybeSpawnWolf(): void {
    if (this.wolf || this.currentAge >= 5) return;
    const lane = (5 + Math.floor(Math.random() * 4)) * (Math.random() < 0.5 ? 1 : -1);
    const horiz = Math.random() < 0.5;
    const from = horiz ? worldToLocal(-9.5, lane) : worldToLocal(lane, -9.5);
    const to = horiz ? worldToLocal(9.5, lane) : worldToLocal(lane, 9.5);
    const spr = makeWolfSprite();
    if (to.x < from.x) spr.scale.x = -Math.abs(spr.scale.x);
    const root = new Container();
    root.addChild(spr);
    root.x = from.x;
    root.y = from.y;
    this.unitsLayer.addChild(root);
    this.wolf = {
      root,
      spr,
      x: from.x,
      y: from.y,
      tx: to.x,
      ty: to.y,
      speed: 55,
      pauseT: 0,
      howled: false,
      half: Math.hypot(to.x - from.x, to.y - from.y) / 2,
      bob: Math.random() * 6,
    };
  }

  private updateWolf(dt: number): void {
    const w = this.wolf;
    if (!w) return;
    w.bob += dt * 9;
    if (w.pauseT > 0) {
      w.pauseT -= dt;
      if (w.pauseT <= 0) w.spr.rotation = 0;
      return;
    }
    const dx = w.tx - w.x;
    const dy = w.ty - w.y;
    const dist = Math.hypot(dx, dy);
    const step = w.speed * dt;
    if (dist <= step) {
      w.root.destroy({ children: true });
      this.wolf = null;
      return;
    }
    if (!w.howled && dist <= w.half) {
      // The silent howl: muzzle to the sky, three breaths rising toward Tengri.
      w.howled = true;
      w.pauseT = 1.2;
      w.spr.rotation = w.spr.scale.x >= 0 ? -0.25 : 0.25;
      this.particles.burst(camera.x + w.x * camera.zoom, camera.y + (w.y - 22) * camera.zoom, {
        count: 3,
        color: 0xf2f2f2,
        speed: 12,
        spread: 8,
        life: 1.4,
        gravity: -30,
        size: 0.7,
      });
      return;
    }
    w.x += (dx / dist) * step;
    w.y += (dy / dist) * step;
    w.root.x = w.x;
    w.root.y = w.y + Math.sin(w.bob) * 1.2;
    w.root.zIndex = w.y;
  }

  /** One rotating, budgeted chimney: every ~0.45s a single era-colored puff
   *  rises from the keep or one of a handful of buildings (max ~2 live
   *  particles at any moment, but the whole town reads as inhabited). */
  private updateSmoke(dt: number): void {
    if (this.currentAge >= 5) return;
    this.smokeTimer -= dt;
    if (this.smokeTimer > 0) return;
    this.smokeTimer = 0.45;
    const sources: { x: number; y: number; h: number }[] = [];
    if (this.townCenter && this.currentAge < 4) {
      sources.push({ x: this.townCenter.x, y: this.townCenter.y, h: this.townCenter.height * 0.85 });
    }
    for (let i = 0; i < this.buildingRecords.length && sources.length < 6; i += 5) {
      const r = this.buildingRecords[i];
      if (this.currentAge===4 && !['mine','lumberCamp'].includes(r.id)) continue;
      sources.push({ x: r.root.x, y: r.root.y, h: r.chimneyH });
    }
    if (!sources.length) return;
    const s = sources[this.smokeIdx++ % sources.length];
    const era = this.currentEra < 0 ? 0 : this.currentEra;
    const color = era >= 4 ? 0xc4c0ff : era >= 3 ? 0xd8dde3 : 0xb8b0a4;
    this.particles.burst(camera.x + s.x * camera.zoom, camera.y + (s.y - s.h) * camera.zoom, {
      count: 1,
      speed: 8,
      spread: 10,
      life: era >= 4 ? 1.2 : 1.8,
      gravity: era >= 4 ? -30 : -16,
      size: 0.8,
      color,
    });
  }

  /** Drives the ground stagger wave, the keep's era-dawn pop, the boss dusk
   *  veil, marching dust + boss stomps, smoke, and the wolf. */
  private updateAmbient(dt: number): void {
    if (this.groundAnim.length) {
      let done = true;
      for (const a of this.groundAnim) {
        a.t += dt;
        const k = Math.min(1, Math.max(0, (a.t - a.delay) / 0.2));
        a.spr.alpha = k;
        a.spr.scale.y = easeOutBack(k); // bouncy pop-in, matching the keep's dawn
        if (k < 1) done = false;
      }
      if (done) this.groundAnim = [];
    }

    if (this.keepPopT >= 0 && this.townCenter) {
      this.keepPopT += dt;
      const p = Math.min(1, this.keepPopT / 0.4);
      this.townCenter.scale.set(0.55 + 0.45 * easeOutBack(p));
      if (p >= 1) {
        this.townCenter.scale.set(1);
        this.keepPopT = -1;
      }
    }

    if (this.duskOverlay && this.duskAlpha !== this.duskTarget) {
      const d = clamp(this.duskTarget - this.duskAlpha, -dt * 0.35, dt * 0.35);
      this.duskAlpha += d;
      if (Math.abs(this.duskTarget - this.duskAlpha) < 0.005) this.duskAlpha = this.duskTarget;
      this.duskOverlay.alpha = this.duskAlpha;
    }

    // Marching feet kick up dust; the boss lands audible-looking stomps.
    const moving = this.marchers.filter(
      (m) => m.phase === 'march' || m.phase === 'rout' || m.phase === 'overrun',
    );
    if (moving.length) {
      this.dustTimer -= dt;
      if (this.dustTimer <= 0) {
        this.dustTimer = 0.12;
        const m = moving[Math.floor(Math.random() * moving.length)];
        this.particles.burst(camera.x + m.x * camera.zoom, camera.y + m.y * camera.zoom, {
          count: 1,
          color: this.currentAge>=5 ? 0x7fd4dc : 0x8a6f4c,
          speed: 18,
          life: 0.35,
          gravity: 60,
          size: 0.5,
        });
      }
      const boss = this.marchers.find((m) => m.kind === 'boss' && m.phase === 'march');
      if (boss && this.currentAge < 5) {
        this.stompTimer -= dt;
        if (this.stompTimer <= 0) {
          this.stompTimer = 0.45;
          this.addShake(0.07);
          this.particles.burst(camera.x + boss.x * camera.zoom, camera.y + boss.y * camera.zoom, {
            count: 2,
            color: 0xb98a55,
            speed: 30,
            life: 0.5,
            gravity: 40,
            size: 0.8,
          });
        }
      }
    }

    this.updateSmoke(dt);
    this.wolfIdleTimer -= dt;
    if (this.wolfIdleTimer <= 0) {
      this.wolfIdleTimer = 90 + Math.random() * 90;
      this.maybeSpawnWolf();
    }
    this.updateWolf(dt);
  }

  private updateUnits(dt: number): void {
    let alive = false;
    let anyFighting = false;
    let anyGone = false;
    // Global floater budget: each spawn rasterizes a canvas Text + GPU upload,
    // and a boss melee fields 20+ units. Cap spawns at ~6/sec total (random
    // fighter each time) instead of rolling per unit (~30/sec at the boss).
    this.floaterBudget += dt * 6;
    // Reused scratch array: this runs every frame during combat, so avoid a
    // fresh allocation per frame (it never escapes this method).
    const fighters = this.fightersScratch;
    fighters.length = 0;
    for (const m of this.marchers) {
      if (m.phase === 'gone') {
        anyGone = true;
        continue;
      }
      alive = true;
      m.bob += dt * 11;

      if (m.phase === 'fight') {
        // Hold the line and trade blows: a forward lunge plus a jitter. The
        // root (and its shadow) stay planted; only the body moves.
        anyFighting = true;
        const melee = ageVisual(this.currentAge).attack === 'melee';
        const lunge = melee ? Math.max(0, Math.sin(m.bob * 3)) * 5 * m.facing : -Math.max(0, Math.sin(m.bob * 2)) * 2 * m.facing;
        m.root.x = m.x;
        m.root.y = m.y;
        m.root.zIndex = m.y;
        m.spr.x = lunge;
        m.spr.y = this.currentAge>=7 ? -4 + Math.round(Math.sin(m.bob*.4)*2) : 0;
        fighters.push(m); // eligible for a Diablo-style floating number below
        continue;
      }

      const dx = m.tx - m.x;
      const dy = m.ty - m.y;
      const dist = Math.hypot(dx, dy);
      const step = m.speed * dt;
      if (dist > step) {
        m.x += (dx / dist) * step;
        m.y += (dy / dist) * step;
      } else {
        m.x = m.tx;
        m.y = m.ty;
        if (m.phase === 'march') {
          m.phase = 'fight'; // reached the line: start the melee
        } else if (m.phase === 'rout' || m.phase === 'overrun') {
          m.root.alpha -= dt * 2.5;
          if (m.root.alpha <= 0) {
            m.phase = 'gone';
            anyGone = true;
          }
        }
      }
      m.root.x = m.x;
      m.root.y = m.y;
      m.root.zIndex = m.y;
      m.spr.x = 0;
      m.spr.y = this.currentAge>=7 ? -4 + Math.round(Math.sin(m.bob*.4)*2) : this.currentAge>=3 && (m.kind==='boss'||m.kind==='brute') ? 0 : Math.round(Math.sin(m.bob));
    }

    // Spend the floater budget on random fighters (density reads the same as
    // the old per-unit rolls, at a bounded raster/upload cost).
    if (fighters.length > 0) {
      while (this.floaterBudget >= 1) {
        this.floaterBudget -= 1;
        this.spawnCombatFloater(fighters[Math.floor(Math.random() * fighters.length)]);
      }
    } else {
      this.floaterBudget = Math.min(this.floaterBudget, 1); // no stockpiling between fights
    }

    // Sparks fly along the clash line for as long as the melee lasts.
    if (anyFighting) {
      this.fightSparkTimer -= dt;
      if (this.fightSparkTimer <= 0) {
        this.fightSparkTimer = 0.06;
        const sx = camera.x + (this.battleX + (Math.random() - 0.5) * 80) * camera.zoom;
        const sy = camera.y + (this.battleY + (Math.random() - 0.5) * 34) * camera.zoom;
        this.particles.burst(sx, sy, {
          color: this.currentAge>=5 ? 0x7fd4dc : Math.random() < 0.5 ? 0xffd24a : 0xff8c3a,
          count: 2,
          speed: 70,
          life: 0.3,
          size: 0.6,
        });
      }
    }

    if (!alive && this.marchers.length) this.clearMarchers();
    else if (anyGone) {
      // Only pay for the filter pass on frames where something actually died.
      this.marchers = this.marchers.filter((m) => {
        if (m.phase === 'gone') {
          m.root.destroy({ children: true });
          return false;
        }
        return true;
      });
    }
  }

  // ---- event-driven juice + combat ----

  private bindEvents(): void {
    bus.on('harvest:hit', ({ gx, gy, resource, crit }) => {
      const p = worldToScreen(gx, gy);
      const rec = this.nodeByTile.get(`${gx},${gy}`);
      if (rec) rec.hitPunch = Math.min(1, rec.hitPunch + (crit ? 0.5 : 0.28));
      if (Math.random() < (crit ? 1 : 0.4)) {
        this.particles.burst(p.x, p.y - 8 * camera.zoom, {
          color: crit ? 0xffe08a : RESOURCES[resource].color,
          count: crit ? 6 : 2,
          speed: crit ? 120 : 70,
          life: 0.4,
          size: 0.7,
        });
      }
    });

    bus.on('harvest:drop', ({ gx, gy, resource, amount }) => {
      const p = worldToScreen(gx, gy);
      const def = RESOURCES[resource];
      this.particles.burst(p.x, p.y - 10 * camera.zoom, {
        color: def.color,
        count: 12,
        speed: 130,
        life: 0.6,
        size: 1,
      });
      this.floaters.spawn(p.x, p.y - 14, `+${fmt(amount)} ${def.icon}`, { color: def.color, size: 17 });
    });

    bus.on('building:built', ({ building }) => {
      const slot = Math.min(this.placedBuildings, BUILDING_SLOTS.length - 1);
      const [gx, gy] = BUILDING_SLOTS[slot];
      this.placeBuilding(building);
      const p = worldToScreen(gx, gy);
      const def = BUILDINGS[building];
      this.particles.burst(p.x, p.y - 6 * camera.zoom, { color: def.color, count: 8, speed: 90, life: 0.5 });
      this.addShake(0.16);
    });

    bus.on('settlement:evolve', ({ tier }) => {
      this.currentTier = tier;
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0xffd24a, count: 40, speed: 220, spread: 160, life: 1.1, size: 1.4 });
      this.addShake(0.6);
      this.addFlash(0xffe08a, 0.35);
    });

    bus.on('card:picked', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0x8fc7ff, count: 30, speed: 200, life: 0.9, size: 1.2 });
      this.addFlash(0x8fc7ff, 0.25);
      this.addShake(0.3);
    });

    bus.on('restart', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0xffd27a, count: 36, speed: 220, life: 1, size: 1.3 });
      this.particles.burst(p.x, p.y, { color: 0x8fc7ff, count: 24, speed: 160, life: 1.1, size: 1.1 });
      this.addFlash(0xffd27a, 0.3);
      this.addShake(0.4);
      this.clearMarchers();
    });

    bus.on('gate:summon', () => this.citizens.shelter(true));
    bus.on('wave:incoming', ({ wave, isBoss }) => {
      this.citizens.shelter(true);
      this.spawnWave(wave, isBoss);
      this.addFlash(0xff5a5a, isBoss ? 0.45 : 0.16);
      this.addShake(isBoss ? 0.4 : 0.2);
      if (isBoss) this.duskTarget = 0.35; // the land darkens while the boss marches
    });

    bus.on('wave:result', ({ win, rewardSpoils, isBoss }) => {
      this.citizens.shelter(false);
      this.resolveCombat(win);
      this.duskTarget = 0; // dawn returns
      if (win) this.maybeSpawnWolf(); // the bozkurt blesses a held wave
      const p = worldToScreen(0, 0);
      if (win) {
        this.addFlash(0x6ad27a, 0.3);
        this.addShake(isBoss ? 0.7 : 0.4);
        this.floaters.spawn(p.x, p.y - 30, `+${fmt(rewardSpoils)} 💎`, { color: 0x7fe0ff, size: 22, life: 1.2 });
      } else {
        this.addFlash(0xff3b3b, 0.55);
        this.addShake(1);
      }
    });

    bus.on('tech:bought', ({ isBuff }) => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, {
        color: isBuff ? 0xffd24a : 0x8fc7ff,
        count: isBuff ? 30 : 14,
        speed: isBuff ? 220 : 130,
        life: 0.8,
        size: 1.1,
      });
      if (isBuff) {
        this.addFlash(0xffd24a, 0.3);
        this.addShake(0.4);
      }
    });

    bus.on('artifact:dropped', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0xffd24a, count: 50, speed: 260, spread: 180, life: 1.3, size: 1.5 });
      this.addFlash(0xffd24a, 0.45);
      this.addShake(0.7);
    });

    bus.on('artifact:activated', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0x7fe0ff, count: 40, speed: 240, life: 1.1, size: 1.3 });
      this.addFlash(0x7fe0ff, 0.35);
      this.addShake(0.5);
    });

    bus.on('conquer:available', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0xc79bff, count: 44, speed: 240, spread: 180, life: 1.2, size: 1.4 });
      this.addFlash(0xc79bff, 0.35);
      this.addShake(0.6);
    });

    bus.on('prestige', () => {
      const p = worldToScreen(0, 0);
      this.particles.burst(p.x, p.y, { color: 0xffd24a, count: 70, speed: 320, spread: 240, life: 1.5, size: 1.7 });
      this.particles.burst(p.x, p.y, { color: 0xb56bff, count: 50, speed: 260, life: 1.6, size: 1.4 });
      this.addFlash(0xffffff, 0.6);
      this.addShake(1);
      this.clearMarchers();
    });

    bus.on('shake', ({ intensity }) => this.addShake(intensity));
    bus.on('flash', ({ color, alpha }) => this.addFlash(color, alpha));
  }

  addShake(amount: number): void {
    this.trauma = Math.min(1, this.trauma + amount);
  }

  addFlash(color: number, alpha: number): void {
    this.flashColor = color;
    this.flashAlpha = Math.max(this.flashAlpha, alpha);
  }

  // ---- per-frame update ----

  update(state: GameState, frameDt: number): void {
    this.currentAge = state.run.age; // keep the era current for live placements
    const simDt = document.hidden || this.lastGameTime < 0 ? 0 : Math.max(0, state.gameTime - this.lastGameTime);
    this.lastGameTime = state.gameTime;
    if (visualAge(state.run.age) !== this.renderedAge) {
      this.renderedAge = visualAge(state.run.age);
      this.eraDawn(state);
    } else if (!this.transition && state.run.settlementTier !== this.renderedTier) this.reskinSettlement(state);
    this.animationClock += frameDt;
    this.updateAgeAnimation();
    this.updateProjectiles(frameDt);
    // The node hints are a teaching aid for the manual phase only: once the
    // workforce gathers on its own, finding nodes by eye stops being the thing
    // the player has to do, so the rings fade out and the island reads clean.
    const hintPulse = state.run.autoUnlocked
      ? 0
      : 0.55 + 0.45 * Math.sin(state.gameTime * 2.4);

    for (const node of state.run.nodes) {
      const rec = this.nodeRecords.get(node.id);
      if (!rec) continue;
      if (rec.lastDepleted && !node.depleted) {
        rec.respawnPop = 0;
        if (rec.gx !== node.gx || rec.gy !== node.gy) {
          // regrew on a new tile (systems/harvest.ts relocateNode)
          this.nodeByTile.delete(`${rec.gx},${rec.gy}`);
          rec.gx = node.gx; rec.gy = node.gy;
          const p = worldToLocal(node.gx, node.gy);
          rec.root.position.set(p.x, p.y);
          rec.root.zIndex = depth(node.gx, node.gy) * 10 + 1;
          this.nodeByTile.set(`${node.gx},${node.gy}`, rec);
        }
      }
      rec.lastDepleted = node.depleted;
      rec.hint.alpha = node.depleted ? 0 : hintPulse;

      if (node.depleted) {
        rec.shape.visible = false;
      } else {
        rec.shape.visible = true;
        const frac = node.maxHp > 0 ? node.hp / node.maxHp : 1;
        if (rec.respawnPop < 1) rec.respawnPop = Math.min(1, rec.respawnPop + frameDt * 4);
        const pop = rec.respawnPop < 1 ? 1 + (1 - rec.respawnPop) * 0.6 : 1;
        if (rec.hitPunch > 0) rec.hitPunch = Math.max(0, rec.hitPunch - frameDt * 6);
        const punch = 1 + rec.hitPunch * 0.25;
        const hpScale = 0.72 + 0.28 * frac;
        rec.shape.scale.set(hpScale * punch * pop, hpScale * (2 - punch) * pop);
        rec.shape.alpha = 0.6 + 0.4 * frac;
      }
    }

    for (const rec of this.buildingRecords) {
      if (rec.popT < 1) {
        rec.popT = Math.min(1, rec.popT + frameDt * 3.5);
        rec.root.scale.set(easeOutBack(rec.popT));
      }
    }

    this.updateUnits(frameDt);
    if (simDt > 0) this.updateWorkers(state, simDt, simDt);
    this.citizens.update(state.run.population, this.transition && !this.transition.rolled ? this.transition.oldAge : this.currentAge, simDt, this.buildingRecords, !!this.transition && !this.transition.rolled);
    this.updateAmbient(frameDt);
    this.updateTransition(document.hidden ? 0 : frameDt);

    if (cursor.active) {
      this.cursorCircle.visible = true;
      const radius = Math.round(harvestRadius(state) * camera.zoom);
      const hot = this.anyNodeUnderCursor(state, radius);
      // Re-tessellating the Graphics every frame feeds the GC and re-uploads
      // geometry 60x/sec (touch keeps the cursor active for the whole session);
      // the shape only changes when radius or the hot flag does.
      if (radius !== this.lastCursorRadius || hot !== this.lastCursorHot) {
        drawCursorCircle(this.cursorCircle, radius, hot);
        this.lastCursorRadius = radius;
        this.lastCursorHot = hot;
      }
      this.cursorCircle.position.set(cursor.x, cursor.y);
    } else {
      this.cursorCircle.visible = false;
    }

    if (this.trauma > 0) {
      const mag = this.trauma * this.trauma * 16;
      this.shakeRoot.position.set((Math.random() * 2 - 1) * mag, (Math.random() * 2 - 1) * mag);
      this.trauma = Math.max(0, this.trauma - frameDt * 1.8);
    } else {
      this.shakeRoot.position.set(0, 0);
    }

    if (this.flashAlpha > 0.001) {
      const w = this.app.screen.width;
      const h = this.app.screen.height;
      this.flashOverlay.clear();
      this.flashOverlay.rect(0, 0, w, h).fill({ color: this.flashColor, alpha: this.flashAlpha });
      this.flashDrawn = true;
      this.flashAlpha = Math.max(0, this.flashAlpha - frameDt * 1.6);
    } else if (this.flashDrawn) {
      this.flashOverlay.clear();
      this.flashDrawn = false;
    }

    this.particles.update(frameDt);
    this.floaters.update(frameDt);
  }

  private anyNodeUnderCursor(state: GameState, radius: number): boolean {
    const rx = radius;
    const ry = radius * 0.5;
    for (const node of state.run.nodes) {
      if (node.depleted) continue;
      const p = worldToScreen(node.gx, node.gy);
      const dx = p.x - cursor.x;
      const dy = p.y - cursor.y;
      if ((dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) <= 1) return true;
    }
    return false;
  }

  toLocal(clientX: number, clientY: number): { x: number; y: number } {
    // getBoundingClientRect forces a synchronous reflow; calling it on every
    // pointermove (60-120Hz on touch) stalls input handling. The canvas rect
    // only changes on resize, so cache it lazily and invalidate in onResize.
    if (!this.canvasRect) this.canvasRect = this.app.canvas.getBoundingClientRect();
    return { x: clientX - this.canvasRect.left, y: clientY - this.canvasRect.top };
  }
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}
